import { useCallback } from 'react';
import { timeToX } from '../utils/coordinate-math';
import type { CycleData } from '../types';

const SNAP_THRESHOLD_PX = 8;

enum SnapPointEnum {
	CYCLE_START = 'cycle-start',
	CYCLE_END = 'cycle-end',
	PLAYHEAD = 'playhead',
	ZERO = 'zero'
}

export interface SnapPoint {
	time: number;
	type: SnapPointEnum;
	cycleId?: string;
}

export interface SnapResult {
	time: number;
	snapped: boolean;
	snapPoint?: SnapPoint;
}

function collectSnapPoints(cycles: CycleData[], currentTime: number, excludeCycleId?: string): SnapPoint[] {
	const points: SnapPoint[] = [
		{ time: 0, type: SnapPointEnum.ZERO },
		{ time: currentTime, type: SnapPointEnum.PLAYHEAD }
	];

	for (const cycle of cycles) {
		if (cycle.id === excludeCycleId) continue;

		points.push(
			{ time: cycle.start, type: SnapPointEnum.CYCLE_START, cycleId: cycle.id },
			{ time: cycle.end, type: SnapPointEnum.CYCLE_END, cycleId: cycle.id }
		);
	}

	return points;
}

function findNearestSnapPoint(
	targetX: number,
	snapPoints: SnapPoint[],
	videoDuration: number,
	canvasWidth: number,
	margin: number
): SnapPoint | null {
	let nearestSnap: SnapPoint | null = null;
	let minDistancePixels = Infinity;

	for (const point of snapPoints) {
		const pointX = timeToX(point.time, videoDuration, canvasWidth, margin);
		const distancePixels = Math.abs(targetX - pointX);

		if (distancePixels <= SNAP_THRESHOLD_PX && distancePixels < minDistancePixels) {
			minDistancePixels = distancePixels;
			nearestSnap = point;
		}
	}

	return nearestSnap;
}

export function useSnap(cycles: CycleData[], currentTime: number, videoDuration: number) {
	const snapTime = useCallback(
		(targetTime: number, canvasWidth: number, margin: number, excludeCycleId?: string): SnapResult => {
			const targetX = timeToX(targetTime, videoDuration, canvasWidth, margin);
			const snapPoints = collectSnapPoints(cycles, currentTime, excludeCycleId);
			const nearestSnap = findNearestSnapPoint(targetX, snapPoints, videoDuration, canvasWidth, margin);

			return nearestSnap
				? { time: nearestSnap.time, snapped: true, snapPoint: nearestSnap }
				: { time: targetTime, snapped: false };
		},
		[cycles, currentTime, videoDuration]
	);

	return { snapTime };
}
